import { AxiosError } from 'axios';
import { useMutation, UseMutationResult, useQuery, UseQueryResult } from 'react-query';
import { api } from '../api';
import { asyncToast, ToastDoneFunction, toastNotification } from '../components/toast/Toast';
import { useWorkpaperContext } from '../providers/WorkpaperProvider';
import queryClient from '../query-client';
import { ManualTrialBalanceRow, TrialBalanceSyncReport, WorkpaperSourceEnum } from '../types/workpaper';
import { WorksheetWithAccount } from '../types/worksheet';

type UseTrialBalanceResult = {
    totalItems: number;
    worksheets: WorksheetWithAccount[];
    lastRefreshedAt: string | undefined;
    ledgerProfitLoss?: number;
    ledgerProfitLossComparative?: number;
    workpaperProfitLoss?: number;
};

type GetTrialBalanceParams = {
    limit: number;
    page: number;
    tab?: string;
    filter?: string;
    search?: string;
    sort?: string;
};

async function getTrialBalance(workpaperId: string, params: GetTrialBalanceParams): Promise<UseTrialBalanceResult> {
    const { data } = await api.get(`/workpapers/${workpaperId}/trial-balance?${params.filter}&${params.sort}`, {
        params: {
            limit: params.limit,
            page: params.page,
            status: params.tab,
            search: params.search,
            searchFilters: params.tab ? 'status' : undefined,
        },
    });
    return data;
}

export function useTrialBalance(params: GetTrialBalanceParams, enabled = true): UseQueryResult<UseTrialBalanceResult, AxiosError> {
    const { id: workpaperId } = useWorkpaperContext();
    return useQuery([workpaperId, 'trial-balance', ...Object.values(params)], () => getTrialBalance(workpaperId, params), {
        keepPreviousData: true,
        enabled,
    });
}

export async function download(workpaperId: string): Promise<void> {
    const response = await api.get(`/workpapers/${workpaperId}/trial-balance/download`, {
        responseType: 'blob',
        headers: { Accept: 'text/csv' },
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'trial-balance.csv');
    document.body.appendChild(link);
    link.click();
}

export function useExportTrialBalance(): UseMutationResult<unknown, AxiosError, string, unknown> {
    return useMutation((workpaperId: string) => download(workpaperId));
}

async function replace(workpaperId: string, trialBalance: ManualTrialBalanceRow[]): Promise<TrialBalanceSyncReport> {
    const { data } = await api.put(`/workpapers/${workpaperId}/trial-balance`, trialBalance);
    return data;
}

export function useReplaceManualTrialBalance(): UseMutationResult<
    TrialBalanceSyncReport,
    AxiosError<{ message: string } | TrialBalanceSyncReport>,
    { workpaperId: string; trialBalance: ManualTrialBalanceRow[] },
    unknown
> {
    return useMutation((params) => replace(params.workpaperId, params.trialBalance));
}

async function sync(workpaperId: string): Promise<{ result: TrialBalanceSyncReport }> {
    const { data } = await api.post(`/workpapers/${workpaperId}/trial-balance/sync`);
    return data;
}

export function useSyncExternalTrialBalance(
    workpaperId?: string
): UseMutationResult<
    { result: TrialBalanceSyncReport },
    AxiosError<{ message: string } | { type: string; message: string; description: string }>,
    boolean | undefined,
    unknown
> {
    let t: ToastDoneFunction;
    return useMutation(() => sync(workpaperId!), {
        mutationKey: 'refresh-trial-balance',
        onMutate: (silent = false) => {
            if (!silent) {
                t = asyncToast('Refreshing trial balance');
            }
        },
        onSuccess: (res) => {
            if (t) {
                t('success', {
                    title: 'Successfully refreshed the trial balance',
                });
            }
        },
        onError: () => {
            if (t) {
                t('error', { title: 'Failed to refresh the trial balance' });
            }
        },
        onSettled: () => {
            queryClient.invalidateQueries(['workpapers', workpaperId]);
            queryClient.invalidateQueries([workpaperId, 'trial-balance']);
        },
    });
}
